import type { FC } from 'react';
import { RestWebServicesWrapper } from './RestWebServices.styled';
import restServices1 from './../../../resources/content/restServices1.png';
import { Table } from 'reactstrap';

interface RestWebServicesProps {}

const RestWebServices: FC<RestWebServicesProps> = () => (
 <RestWebServicesWrapper>
   <header className="entry-header">
      <h1 className="entry-title" itemProp="headline">Restful Web Services</h1>			
   </header> 
   <br/>
   <div itemProp="text">
      <ul className="text-align-justify">
         <li>REST stands for REpresentational State Transfer.</li>
         <li>In REST architecture, clients and servers exchange representations of resources by using a standardized interface and protocol.</li>
         <li>In REST Architecture everything is a resource and to access a resource by a common interface, we use HTTP standard methods.&nbsp;</li>
         <li>A web service is a collection of open protocols and standards. It helps to exchange data between applications or systems. REST Architecture based web services are known as RESTful web services.</li>
         <li>The REST Server provides access to resources and the REST client accesses and modifies the resources.</li>
         <li>Each resource is identified by URIs/ global IDs. </li>
         <li>REST uses many different representations to represent a resource like text, JSON, XML. JSON is the most popular one.</li>
         <li>The four HTTP methods most commonly used in REST based architecture:
            <Table bordered striped className="text-align-justify">
               <tbody>
                  <tr>
                     <th><strong>GET</strong></th>
                     <td>It’s purpose is to provide read-only access to a resource.</td>
                  </tr>
                  <tr>
                     <th><strong>POST</strong></th>
                     <td>It’s purpose is to create a new resource.</td>
                  </tr>
                  <tr>
                     <th><strong>DELETE</strong></th>
                     <td>It’s purpose is to remove an existing resource.</td>
                  </tr>
                  <tr>
                     <th><strong>PUT</strong></th>
                     <td>It’s purpose is to update an existing resource or create a new resource.</td>
                  </tr>
               </tbody>
            </Table>
         </li>
      </ul>
      <br />
      <h4 className="text-align-justify"><strong>Restful Web Services</strong></h4>
      <p className="text-align-justify">RESTful Web Services&nbsp;is a lightweight, maintainable, and scalable service that is built on the REST architecture. It exposes API from your&nbsp;application&nbsp;in a secure, uniform, stateless manner to the calling client. Using the Restful service, the calling client can perform predefined operations. The REST&nbsp;architecture design allows us to use the stateless protocol of HTTP. &nbsp;</p>
      <ul className="text-align-justify">
         <li>It is language and platform-independent.</li>
         <li>It should not keep a client state on the server. This restriction is nothing but Statelessness. The client is responsible to pass its context to the server and then the server can store this context to process the client’s further request.</li>
         <li>It can be configured for internalization. Internationalization is the process of designing web applications or services in such a way that it can provide support for various countries, various languages automatically without making the changes in the application. It is also known as&nbsp;I18N&nbsp;because the word internationalization has a total of 18 characters starting from&nbsp;I&nbsp;to&nbsp;N.</li>
         <li>It basically defines a URI, Uniform Resource Identifier. In other words, a service that provides resource representation such as JSON and a set of HTTP Methods.</li>
         <li>To create a URI, Use Plural Noun,&nbsp;Avoid using spaces, &nbsp;Use lowercase letters, Maintain Backward Compatibility, and Use HTTP Verb.</li>
         <li>HTTP defines the following standard status code –
            <ul>
               <li><strong>404:</strong>&nbsp;RESOURCE NOT FOUND</li>
               <li><strong>200:</strong>&nbsp;SUCCESS</li>
               <li><strong>201:</strong>&nbsp;CREATED</li>
               <li><strong>401:</strong>&nbsp;UNAUTHORIZED</li>
               <li><strong>500:</strong>&nbsp;SERVER ERROR</li>
            </ul>
         </li>
      </ul>
      <br />
      <h4 className="text-align-justify"><strong>Annotaions</strong></h4>
      <ol className="text-align-justify">
         <li>
            <strong><mark style={{backgroundColor:'rgba(0, 0, 0, 0)',color:'#647f19'}} className="has-inline-color">@Path </mark></strong>
            <ul className="is-style-checkbox">
               <li>To specify the relative path of class and methods. By scanning the Path annotation value, one can get the URI of a web service.</li>
            </ul>
         </li>
         <li>
            <strong><mark style={{backgroundColor:'rgba(0, 0, 0, 0)',color:'#647f19'}} className="has-inline-color">@GET, @PUT, @POST, @DELETE, @HEAD </mark></strong>
            <ul className="is-style-checkbox">
               <li>To specify the HTTP request type for a method.</li>
            </ul>
         </li>
         <li>
            <strong><mark style={{backgroundColor:'rgba(0, 0, 0, 0)',color:'#647f19'}} className="has-inline-color">@PRODUCES, @CONSUMES </mark></strong>
            <ul className="is-style-checkbox">
               <li>To specify the request and response types.</li>
            </ul>
         </li>
         <li>
            <strong><mark style={{backgroundColor:'rgba(0, 0, 0, 0)',color:'#647f19'}} className="has-inline-color">@PathParam </mark></strong>
            <ul className="is-style-checkbox">
               <li>To bind the method parameter to path value by parsing it. In other words, to map the variable URI path fragments into your method call.</li>
            </ul>
         </li>
         <li>
            <strong><mark style={{backgroundColor:'rgba(0, 0, 0, 0)',color:'#647f19'}} className="has-inline-color">@PathVariable </mark></strong>
            <ul className="is-style-checkbox">
               <li>For data passed in the URI (e.g. RESTful web services).</li>
            </ul>
         </li>
         <li>
            <strong><mark style={{backgroundColor:'rgba(0, 0, 0, 0)',color:'#647f19'}} className="has-inline-color">@RequestParam </mark></strong>
            <ul className="is-style-checkbox">
               <li>To extract the data found in query parameters.</li>
            </ul>
         </li>
         <li>
            <strong><mark style={{backgroundColor:'rgba(0, 0, 0, 0)',color:'#647f19'}} className="has-inline-color">@QueryParam</mark></strong>
            <ul className="is-style-checkbox">
               <li>To access key/value pairs in the query string of the URL (the part after the ?).</li>
            </ul>
         </li>
      </ol>
      <br />
      <figure className="aligncenter size-large is-style-default">
         <img decoding="async" src={restServices1} data-lazy-type="image" alt="" className="reduced-img"/>
      </figure>
      <br/>

      <Table bordered striped className="text-align-justify">
         <thead>
            <tr>
               <th  className="text-align-center" scope="row" style={{ width: '50%'}}><strong>SOAP</strong></th>
               <th  className="text-align-center" scope="row" style={{ width: '50%'}}><strong>REST</strong></th>
            </tr>
         </thead>
         <tbody>
            <tr>
               <td>It is a protocol.</td>
               <td>It is an architectural style.</td>
            </tr>
            <tr>
               <td>Server and client applications are tightly coupled and bind with the WSDL contract.</td>
               <td>There is no contract in REST web services and clients.</td>
            </tr>
            <tr>
               <td>The request and response type can be XML only.</td>
               <td>Request and response types can be XML, JSON, text, etc.</td>
            </tr>
            <tr>
               <td>JAX-WS is the Java API for SOAP web services.</td>
               <td>JAX-RS is the Java API for REST web services.</td>
            </tr>
         </tbody>
      </Table>
   </div>
 </RestWebServicesWrapper>
);

export default RestWebServices;
